import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  deleteDoc,
  Timestamp,
  addDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  updateEmail as firebaseUpdateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser as firebaseDeleteUser,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore as db, auth, storage, functions } from '../database/firebase';

// Collection names for all user types (main user collections)
const USER_COLLECTIONS = ['admin', 'alumni', 'clients', 'coaches', 'moderator', 's_admin'];

// All collections that may contain email references
const EMAIL_COLLECTIONS = [
  { name: 'admin', fields: ['email'] },
  { name: 'alumni', fields: ['email'] },
  { name: 'alumniReports', fields: ['email'] },
  { name: 'bif_applications', fields: ['email'] },
  { name: 'clients', fields: ['email'] },
  { name: 'coaches', fields: ['email'] },
  { name: 'coachingLog', fields: ['coachEmail'] },
  { name: 'commercialisation', fields: ['userEmail'] },
  { name: 'commercialisationReadiness', fields: ['userEmail'] },
  { name: 'companyProfiles', fields: ['email'] },
  { name: 'documentBank', fields: ['ownerEmail'] },
  { name: 'documentBankViews', fields: ['clientEmail'] },
  { name: 'entrepreneurProfiles', fields: [] }, // userId only
  { name: 'gtmStrategyPlan', fields: ['userEmail'] },
  { name: 'moderator', fields: ['email'] },
  { name: 'moderatorReports', fields: ['email'] },
  { name: 'organizations', fields: ['ownerEmail'] },
  { name: 'productAudits', fields: ['userEmail'] },
  { name: 'productDevelopmentSubmissions', fields: ['userEmail'] },
  { name: 'product_development', fields: ['userEmail'] },
  { name: 's_admin', fields: ['email'] },
  { name: 'stageAuditLogs', fields: ['userEmail'] },
  { name: 'startupLaunchChecklist', fields: ['userEmail'] },
  { name: 'teamMembers', fields: ['email'] },
  { name: 'users', fields: ['email'] },
];

/**
 * Find user's collection and document
 */
export async function findUserCollection(uid) {
  for (const collectionName of USER_COLLECTIONS) {
    const docRef = doc(db, collectionName, uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { collection: collectionName, data: docSnap.data() };
    }
  }
  return null;
}

/**
 * Update user profile
 */
export async function updateProfile({ uid, displayName, bio, photoURL }) {
  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  const updates = {};
  if (displayName !== undefined) updates.displayName = displayName;
  if (bio !== undefined) updates.bio = bio;
  if (photoURL !== undefined) updates.photoURL = photoURL;
  updates.updatedAt = Timestamp.now();

  const docRef = doc(db, userInfo.collection, uid);
  await updateDoc(docRef, updates);

  // Log activity
  await logSecurityActivity({
    uid,
    action: 'profile_updated',
    details: 'Profile information updated',
  });

  return updates;
}

/**
 * Update user profile with extended fields (all collections)
 */
export async function updateProfileExtended({
  uid,
  firstname,
  lastname,
  displayName,
  bio,
  photoURL,
  phone,
  location,
  organization,
  company,
  department,
  employeeId,
  specialization,
  prefix,
  gender,
}) {
  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  const updates = {};

  // Basic fields
  if (firstname !== undefined) updates.firstname = firstname;
  if (lastname !== undefined) updates.lastname = lastname;
  if (displayName !== undefined) updates.displayName = displayName;
  if (bio !== undefined) updates.bio = bio;
  if (photoURL !== undefined) updates.photoURL = photoURL;

  // Contact fields
  if (phone !== undefined) updates.phone = phone;
  if (location !== undefined) updates.location = location;

  // Professional fields
  if (organization !== undefined) updates.organization = organization;
  if (company !== undefined) updates.company = company;
  if (department !== undefined) updates.department = department;
  if (employeeId !== undefined) updates.employeeId = employeeId;
  if (specialization !== undefined) updates.specialization = specialization;

  // Personal fields
  if (prefix !== undefined) updates.prefix = prefix;
  if (gender !== undefined) updates.gender = gender;

  // Auto-generate name if not provided
  if (!updates.displayName && (firstname || lastname)) {
    const first = firstname || userInfo.data.firstname || '';
    const last = lastname || userInfo.data.lastname || '';
    updates.displayName = `${first} ${last}`.trim();
  }

  // Auto-generate name field for backwards compatibility
  if (firstname || lastname) {
    const first = firstname || userInfo.data.firstname || '';
    const last = lastname || userInfo.data.lastname || '';
    updates.name = `${first} ${last}`.trim();
  }

  updates.updatedAt = Timestamp.now();

  const docRef = doc(db, userInfo.collection, uid);
  await updateDoc(docRef, updates);

  // Log activity
  await logSecurityActivity({
    uid,
    action: 'profile_updated',
    details: 'Profile information updated',
    metadata: { fieldsUpdated: Object.keys(updates) },
  });

  return updates;
}

export async function uploadProfilePhoto(uid, file) {
  if (!file) throw new Error('No file provided');

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Please upload an image (JPEG, PNG, GIF, or WebP)');
  }

  // Validate file size (5MB max)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('File too large. Maximum size is 5MB');
  }

  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  // Delete old photo if exists
  if (userInfo.data.photoURL) {
    try {
      const oldPhotoRef = ref(storage, userInfo.data.photoURL);
      await deleteObject(oldPhotoRef);
    } catch (error) {
      // Old photo deletion failed (may not exist) - ignore
    }
  }

  // Upload new photo
  const timestamp = Date.now();
  const fileExtension = file.name.split('.').pop();
  const fileName = `profile_${uid}_${timestamp}.${fileExtension}`;
  const storageRef = ref(storage, `profiles/${fileName}`);

  await uploadBytes(storageRef, file);
  const photoURL = await getDownloadURL(storageRef);

  // Update user document
  await updateProfile({ uid, photoURL });

  return photoURL;
}

/**
 * Change user email across all collections
 */
export async function changeEmail({ uid, currentPassword, newEmail }) {
  const user = auth.currentUser;
  if (!user || user.uid !== uid) {
    throw new Error('Authentication required');
  }

  // Validate new email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(newEmail)) {
    throw new Error('Invalid email format');
  }

  // Check if email is already in use
  for (const collectionName of USER_COLLECTIONS) {
    const q = query(collection(db, collectionName), where('email', '==', newEmail));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      throw new Error('Email already in use');
    }
  }

  // Reauthenticate user FIRST
  try {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  } catch (error) {
    if (error.code === 'auth/wrong-password') {
      throw new Error('Incorrect password');
    }
    throw new Error('Authentication failed: ' + error.message);
  }

  const oldEmail = user.email;
  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  const updatedCollections = new Set();
  const batch = writeBatch(db);

  // Update ALL collections with email references
  for (const { name: collectionName, fields } of EMAIL_COLLECTIONS) {
    for (const field of fields) {
      try {
        const q = query(collection(db, collectionName), where(field, '==', oldEmail));
        const snapshot = await getDocs(q);

        snapshot.forEach(docSnap => {
          const docRef = doc(db, collectionName, docSnap.id);
          const updates = {
            [field]: newEmail,
            oldEmail: oldEmail, // Preserve old email
            emailUpdatedAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          };
          batch.update(docRef, updates);
          updatedCollections.add(collectionName);
        });
      } catch (error) {
        // Skip collections with indexing issues or that don't exist
        // Error silently ignored
      }
    }
  }

  // Commit Firestore batch FIRST
  await batch.commit();

  // Update Firebase Auth email LAST
  // Note: Firebase may require email verification depending on project settings
  try {
    await user.reload();

    // Try direct email update first
    try {
      await firebaseUpdateEmail(user, newEmail);
    } catch (directUpdateError) {
      // If direct update fails with operation-not-allowed, try verification flow
      if (directUpdateError.code === 'auth/operation-not-allowed') {
        // Send verification email to new address
        await verifyBeforeUpdateEmail(user, newEmail);

        // Log that verification is pending
        await logSecurityActivity({
          uid,
          action: 'email_verification_sent',
          details: `Verification email sent to ${newEmail}. User must verify before email change completes.`,
          metadata: { oldEmail, newEmail, status: 'pending_verification' },
        });

        // Return with verification required flag
        return {
          success: true,
          verificationRequired: true,
          updatedCollections: Array.from(updatedCollections),
          oldEmail,
          message:
            'Email updated in database. Please check your new email inbox and verify to complete the change.',
        };
      }
      throw directUpdateError;
    }
  } catch (authError) {
    // Firebase Auth update error - re-thrown below

    // Log partial update
    await logSecurityActivity({
      uid,
      action: 'email_update_firestore_only',
      details: `Email updated in Firestore collections only. Auth update requires verification.`,
      metadata: {
        oldEmail,
        newEmail,
        error: authError.code,
        collections: Array.from(updatedCollections),
      },
    });

    // Still return success since Firestore is updated
    return {
      success: true,
      verificationRequired: false,
      firestoreOnly: true,
      updatedCollections: Array.from(updatedCollections),
      oldEmail,
      message: 'Email updated in all database collections. You can now login with your new email.',
    };
  }

  // Log successful activity
  await logSecurityActivity({
    uid,
    action: 'email_changed',
    details: `Email changed from ${oldEmail} to ${newEmail}`,
    metadata: {
      oldEmail,
      newEmail,
      updatedCollections: Array.from(updatedCollections),
      totalCollections: updatedCollections.size,
    },
  });

  return {
    success: true,
    updatedCollections: Array.from(updatedCollections),
    oldEmail,
  };
}

/**
 * Change password
 */
export async function changePassword({ uid, currentPassword, newPassword }) {
  const user = auth.currentUser;
  if (!user || user.uid !== uid) {
    throw new Error('Authentication required');
  }

  // Validate new password
  if (newPassword.length < 8) {
    throw new Error('Password must be at least 8 characters long');
  }

  // Reauthenticate
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);

  // Update password
  await updatePassword(user, newPassword);

  // Update last password change timestamp
  const userInfo = await findUserCollection(uid);
  if (userInfo) {
    const docRef = doc(db, userInfo.collection, uid);
    await updateDoc(docRef, {
      lastPasswordChange: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  await logSecurityActivity({
    uid,
    action: 'password_changed',
    details: 'Password updated successfully',
  });

  return { success: true };
}

/**
 * Enable/Disable 2FA
 */
export async function toggle2FA({ uid, enabled, secret, backupCodes }) {
  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  const updates = {
    twoFactorEnabled: enabled,
    updatedAt: Timestamp.now(),
  };

  if (enabled) {
    updates.twoFactorSecret = secret;
    updates.backupCodes = backupCodes;
    updates.twoFactorEnabledAt = Timestamp.now();
  } else {
    updates.twoFactorSecret = null;
    updates.backupCodes = null;
  }

  const docRef = doc(db, userInfo.collection, uid);
  await updateDoc(docRef, updates);

  await logSecurityActivity({
    uid,
    action: enabled ? '2fa_enabled' : '2fa_disabled',
    details: `Two-factor authentication ${enabled ? 'enabled' : 'disabled'}`,
  });

  return { success: true };
}

/**
 * Enable/Disable per-user login OTP (email-based).
 * This flag is stored on the user's document in their primary collection
 * (admin, alumni, clients, coaches, moderator, s_admin).
 */
export async function toggleLoginOtp({ uid, enabled }) {
  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  const updates = {
    loginOtpEnabled: !!enabled,
    updatedAt: Timestamp.now(),
  };

  if (enabled) {
    updates.loginOtpEnabledAt = Timestamp.now();
  }

  const docRef = doc(db, userInfo.collection, uid);
  await updateDoc(docRef, updates);

  await logSecurityActivity({
    uid,
    action: enabled ? 'login_otp_enabled' : 'login_otp_disabled',
    details: `Login OTP ${enabled ? 'enabled' : 'disabled'} for this account`,
  });

  return { success: true };
}

/**
 * Generate 2FA secret and backup codes
 */
export function generate2FASecret() {
  // Generate a random base32 secret
  const base32chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  for (let i = 0; i < 32; i++) {
    secret += base32chars.charAt(Math.floor(Math.random() * base32chars.length));
  }

  // Generate 10 backup codes
  const backupCodes = [];
  for (let i = 0; i < 10; i++) {
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();
    backupCodes.push(code);
  }

  return { secret, backupCodes };
}

/**
 * Delete user account
 */
export async function deleteAccount({ uid, password, reason }) {
  const user = auth.currentUser;
  if (!user || user.uid !== uid) {
    throw new Error('Authentication required');
  }

  // Reauthenticate
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);

  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  await logSecurityActivity({
    uid,
    action: 'account_deleted',
    details: `Account deletion requested. Reason: ${reason || 'Not provided'}`,
    metadata: {
      collection: userInfo.collection,
      email: userInfo.data.email,
      reason,
    },
  });

  const docRef = doc(db, userInfo.collection, uid);
  await deleteDoc(docRef);

  if (userInfo.data.photoURL) {
    try {
      const photoRef = ref(storage, userInfo.data.photoURL);
      await deleteObject(photoRef);
    } catch (error) {}
  }

  await firebaseDeleteUser(user);

  return { success: true };
}

/**
 * Log security activity
 */
export async function logSecurityActivity({ uid, action, details, metadata = {} }) {
  try {
    await addDoc(collection(db, 'security_logs'), {
      uid,
      action,
      details,
      metadata,
      timestamp: Timestamp.now(),
      ipAddress: null,
      userAgent: navigator.userAgent,
    });
  } catch (error) {
    // Failed to log security activity - silently ignore to prevent recursion
  }
}

/**
 * Get security activity logs
 */
export async function getSecurityLogs(uid, limit = 20) {
  const q = query(
    collection(db, 'security_logs'),
    where('uid', '==', uid)
    // orderBy('timestamp', 'desc'),
    // limit(limit)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Send password reset email
 */
export async function sendPasswordReset(email) {
  await sendPasswordResetEmail(auth, email);
  return { success: true };
}

/**
 * Client OTP (login) configuration – read-only for end users.
 * This reflects the global setting controlled from the admin Settings panel.
 */
export async function getClientOtpSettings() {
  const callable = httpsCallable(functions, 'getClientOtpSettings');
  const res = await callable();
  return res.data;
}

export async function requestClientLoginOtp(email) {
  try {
    const callable = httpsCallable(functions, 'requestClientLoginOtp');
    const res = await callable({ email });
    return res.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Verify a client login OTP against a previously issued request.
 */
export async function verifyClientLoginOtp({ otpRequestId, email, otp }) {
  try {
    const callable = httpsCallable(functions, 'verifyClientLoginOtp');
    const res = await callable({ otpRequestId, email, otp });
    return res.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Send a password reset OTP to the user's email.
 */
export async function sendPasswordResetOtp(email) {
  const callable = httpsCallable(functions, 'sendPasswordResetOtp');
  const res = await callable({ email });
  return res.data;
}

/**
 * Verify password reset OTP without resetting password (for two-step flow).
 */
export async function verifyPasswordResetOtpOnly({ otpRequestId, email, otp }) {
  const callable = httpsCallable(functions, 'verifyPasswordResetOtpOnly');
  const res = await callable({ otpRequestId, email, otp });
  return res.data;
}

/**
 * Verify password reset OTP and reset the user's password.
 */
export async function verifyPasswordResetOtp({ otpRequestId, email, otp, newPassword }) {
  const callable = httpsCallable(functions, 'verifyPasswordResetOtp');
  const res = await callable({ otpRequestId, email, otp, newPassword });
  return res.data;
}

/**
 * Send a simple OTP email (for compatibility with TypeScript implementation).
 * Note: Returns OTP in response for compatibility, but consider using requestClientLoginOtp for production.
 */
export async function sendOtpEmail(email) {
  const callable = httpsCallable(functions, 'sendOtpEmail');
  const res = await callable({ email });
  return res.data;
}

/**
 * Verify a simple OTP email (for compatibility with sendOtpEmail).
 */
export async function verifyOtpEmail({ email, otp, otpRequestId }) {
  const callable = httpsCallable(functions, 'verifyOtpEmail');
  const res = await callable({ email, otp, otpRequestId });
  return res.data;
}

/**
 * Submit registration with optional two-step OTP verification.
 * If twoFactorEnabled=true, sends OTP email and waits for verification.
 * If twoFactorEnabled=false, saves registration and immediately emails confirmation.
 */
export async function submitRegistration({
  fullName,
  email,
  phone,
  program,
  twoFactorEnabled = false,
  metadata = {},
}) {
  const callable = httpsCallable(functions, 'submitRegistration');
  const res = await callable({
    fullName,
    email,
    phone,
    program,
    twoFactorEnabled,
    metadata,
  });
  return res.data;
}

/**
 * Verify registration OTP and complete the registration submission.
 */
export async function verifyRegistrationOtp({ registrationId, email, otp }) {
  const callable = httpsCallable(functions, 'verifyRegistrationOtp');
  const res = await callable({ registrationId, email, otp });
  return res.data;
}

/**
 * Get user settings
 */
export async function getUserSettings(uid) {
  const userInfo = await findUserCollection(uid);
  if (!userInfo) throw new Error('User not found');

  return {
    collection: userInfo.collection,
    ...userInfo.data,
  };
}
